import { Injectable } from '@angular/core';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { catchError, of, switchMap } from 'rxjs';
import { lastValueFrom } from 'rxjs';

const translationBaseUrl = environment.translationApiUrl;
const coreBaseUrl = String(environment.cncsCoreUrl ?? '').trim().replace(/\/+$/, '');
const userBaseLanguageEndpoint = `${coreBaseUrl}/api/users/me/base-language`;

export interface LanguageItem {
  id: number;
  createdAt: string;
  updatedAt: string;
  disabledAt: string | null;
  active: boolean;
  languageFull: string;
  languageFullNative: string;
  languageISO2: string;
  languageISO3: string;
  flagImage: string;
  flagCode: string;
  isBaseLanguage: boolean;
  isPopularLanguage: boolean;
}

export interface LanguagesResponse {
  data: LanguageItem[];
  meta: { itemCount: number };
}

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  rtlLangs = ['ur', 'ar'];
  langLocalStorageSet$ = new Subject<void>();
  public uiLanguageChanged$ = new Subject<string>();

  constructor(
    public translate: TranslateService,
    private httpClient: HttpClient
  ) {
    // Don't initialize here - let APP_INITIALIZER handle it
    translate.addLangs(['en', 'fr']);
  }

  initialize() {
    const fallbackLanguage = this.normalizeLanguageCode(localStorage.getItem('language') || 'en');

    const initialLanguage$ = this.hasAuthToken()
      ? this.httpClient
          .get<{ data?: { base_language?: string } }>(userBaseLanguageEndpoint)
          .pipe(
            map((res) => this.normalizeLanguageCode(res?.data?.base_language || fallbackLanguage)),
            catchError(() => of(fallbackLanguage))
          )
      : of(fallbackLanguage);

    return initialLanguage$.pipe(
      switchMap((currentLanguage) => this.translate.use(currentLanguage)),
      map(() => {
        const currentLanguage = this.normalizeLanguageCode(
          this.translate.currentLang ?? this.translate.getCurrentLang() ?? fallbackLanguage
        );
        localStorage.setItem('language', currentLanguage);
        this.setRtlDirection();
        this.langLocalStorageSet$.next();
      })
    );
  }

  getLanguages(params: HttpParams) {
    return this.httpClient
      .get<LanguagesResponse>(`${translationBaseUrl}/languages`, { params })
      .pipe(map((res) => res?.data ?? []));
  }

  getUiLanguageChangeObservable() {
    return this.uiLanguageChanged$;
  }

  useLang(lang: string) {
    const normalizedLang = this.normalizeLanguageCode(lang);

    this.translate.use(normalizedLang).subscribe(() => {
      localStorage.setItem('language', normalizedLang);
      this.setRtlDirection();
      this.langLocalStorageSet$.next();
    });
  }

  setRtlDirection() {
    const currentLang = this.translate.currentLang ?? this.translate.getCurrentLang();
    const dir = this.rtlLangs.includes(currentLang) ? 'rtl' : 'ltr';
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.setAttribute('dir', dir);
    }
  }

  setTranslationTo(lang: string, persistToApi: boolean = true) {
    const normalizedLang = this.normalizeLanguageCode(lang);

    this.useLang(normalizedLang);
    this.uiLanguageChanged$.next(normalizedLang);

    if (persistToApi && this.hasAuthToken()) {
      this.httpClient
        .put<{ data?: { base_language?: string } }>(userBaseLanguageEndpoint, {
          base_language: normalizedLang,
        })
        .subscribe({
          error: (error) => {
            console.warn('[TranslationService] Failed to persist base language.', error);
          },
        });
    }
  }

  async syncBaseLanguageFromServer(fallbackLanguage?: string): Promise<string> {
    const fallback = this.normalizeLanguageCode(
      fallbackLanguage || localStorage.getItem('language') || 'en'
    );

    let resolvedLanguage = fallback;

    if (this.hasAuthToken()) {
      try {
        const res = await lastValueFrom(
          this.httpClient.get<{ data?: { base_language?: string } }>(userBaseLanguageEndpoint)
        );

        resolvedLanguage = this.normalizeLanguageCode(res?.data?.base_language || fallback);
      } catch {
        resolvedLanguage = fallback;
      }
    }

    this.setTranslationTo(resolvedLanguage, false);

    return resolvedLanguage;
  }

  getInterfaceTranslationJson(lang: string) {
    const apiAddress = `${translationBaseUrl}/interface-translation-value/json/${environment.cncsApplicationId}/${lang}`;
    return this.httpClient.get<Record<string, unknown>>(apiAddress);
  }

  reloadInterfaceTranslationJson() {
    const lang = this.translate.currentLang ?? this.translate.getCurrentLang();
    return this.translate.reloadLang(lang).pipe(
      map(() => {
        this.setRtlDirection();
      })
    );
  }

  private hasAuthToken(): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }

    const token = localStorage.getItem('auth_token');

    return typeof token === 'string' && token.trim().length > 0;
  }

  private normalizeLanguageCode(lang: string | null | undefined): string {
    const normalized = String(lang ?? 'en').trim().toLowerCase();

    if (!normalized) {
      return 'en';
    }

    if (normalized === 'kr' || normalized === 'mu') {
      return 'mfe';
    }

    if ((normalized.length === 2 || normalized.length === 3) && /^[a-z]+$/.test(normalized)) {
      return normalized;
    }

    return 'en';
  }
}
